import { FC } from 'react'
import db from 'mobile_web/modules/db'
import { useLiveQuery } from 'dexie-react-hooks'
import { addToRecentlyEarnedBadges } from '../queries'
import Modal from '../../common/components/modal'
import { BadgeModalContainer as Container } from 'mytours-read-common'
import { Button, Card, CardContent, CardHeader } from '@mytours/components'
import NavigationService from 'mobile_web/modules/navigation/NavigationService'
import { Badge, GetCollectionItemsIndexResponseData } from 'mytours-read-api-typescript'
import { ImageSizePreset } from '../../../common_lib/types'
import { generateImageSrcSet, generateImageUrl } from '../../../common_lib/utils'
import strings from 'mobile_web/strings'
import badgeLockedImage from '../../../assets/badge-icon-locked.png'
import classNames from 'classnames'

interface BadgeData {
	linkId: number
	linkType: Badge.EarnedLinkTypeEnum | Badge.UnearnedLinkTypeEnum
	linkSubtype: Badge.EarnedLinkSubtypeEnum | Badge.UnearnedLinkSubtypeEnum
	linkVersion: number
	title: string
	body: string
	buttonLabel: string
}

const BadgeModal: FC<Container.Props & Container.Actions> = ({
	badgeId,
	toggleBadgeModal,
	showModal,
	earned,
	shouldBePushedToEarned,
	appManifest,
}) => {
	const earnedValue = earned ? 'earned' : 'unearned'
	const badge = useLiveQuery(() => badgeId && db.badges.where({ id: badgeId }).first(), [badgeId])
	const accountId = badge
		? appManifest?.badge_groups?.find(badgeGroup => badgeGroup.id === badge.group_id)?.account_id
		: undefined
	const modalContent: BadgeData = badge
		? {
				linkId: badge[`${earnedValue}_link_id`],
				linkType: badge[`${earnedValue}_link_type`],
				linkSubtype: badge[`${earnedValue}_link_subtype`],
				linkVersion: badge[`${earnedValue}_link_version`],
				title: badge[`${earnedValue}_title`],
				body: badge[`${earnedValue}_body`],
				buttonLabel: badge[`${earnedValue}_link_label`],
			}
		: undefined

	const focalPoint = badge?.badge_media_item?.file?.focal_point
	const crop = focalPoint && (focalPoint[0] !== 0.5 || focalPoint[1] !== 0.5)
	const image = badge?.badge_media_item?.file
		? generateImageUrl(badge.badge_media_item?.file, {
				imageSizePreset: ImageSizePreset.Grid,
				crop,
				focalPoint,
			})
		: badgeLockedImage
	const imageSrcSet = badge?.badge_media_item?.file
		? generateImageSrcSet(badge.badge_media_item?.file, {
				imageSizePreset: ImageSizePreset.Grid,
				crop,
				focalPoint,
			})
		: badgeLockedImage

	const handleCloseModal = () => {
		toggleBadgeModal(false, badgeId, shouldBePushedToEarned, earned)
	}

	const openModal = (): void => {
		const subtype = modalContent?.linkSubtype?.split('_').reverse()[0]
		handleCloseModal()
		NavigationService.pushModalWindow(
			modalContent?.linkType as unknown as GetCollectionItemsIndexResponseData.ItemTypeEnum,
			subtype as GetCollectionItemsIndexResponseData.ItemSubtypeEnum,
			modalContent?.linkId,
			modalContent?.linkVersion,
			accountId,
		)
	}

	const renderButton =
		!!modalContent?.linkId && !!modalContent?.linkVersion && !!modalContent?.linkType && !!modalContent?.linkSubtype

	const onDidDismiss = () => {
		if (shouldBePushedToEarned) {
			addToRecentlyEarnedBadges(badgeId)
		}
		toggleBadgeModal(false, undefined, false, true)
	}

	return (
		<Modal isOpen={showModal} onDidDismiss={onDidDismiss} cssClass="badge-modal">
			<Card variant="ios">
				<CardHeader>
					<Button variant="clear" className="close" onClick={handleCloseModal} aria-label={strings.close}>
						<i className="fal fa-xmark" />
					</Button>
				</CardHeader>
				<CardContent className="badge-modal-content">
					<div className={classNames('badge-modal-image', { 'badge-modal-image--unearned': !earned })}>
						<img src={image} srcSet={imageSrcSet} alt="" />
					</div>
					<h2>{modalContent?.title}</h2>
					<div dangerouslySetInnerHTML={{ __html: modalContent?.body || '' }} />
					{renderButton && (
						<Button variant="clear" aria-label={modalContent?.buttonLabel} onClick={openModal} className="-submit">
							{modalContent?.buttonLabel}
						</Button>
					)}
				</CardContent>
			</Card>
		</Modal>
	)
}

const BadgeModalComponent = Container.default(BadgeModal)
export default BadgeModalComponent
